import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Battery, CircleAlert, Clock, Droplets, Loader2, MapPin, RefreshCw, Signal, Thermometer } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, Panel, Overline } from "@/components/Panel";
import { MildewStatus } from "@/components/MildewStatus";
import { TreepletApiError, getDevice, getDeviceLatest, getDeviceTelemetry } from "@/api/treeplet";

const TIME_WINDOWS = {
  "24h": { label: "Últimas 24 horas", durationMs: 24 * 60 * 60 * 1000, interval: "15m" },
  "7d": { label: "Últimos 7 días", durationMs: 7 * 24 * 60 * 60 * 1000, interval: "hourly" },
  "30d": { label: "Últimos 30 días", durationMs: 30 * 24 * 60 * 60 * 1000, interval: "daily" },
  "90d": { label: "Últimos 90 días", durationMs: 90 * 24 * 60 * 60 * 1000, interval: "daily" },
};

const CHART_METRICS = {
  temperature: {
    label: "Temperatura",
    unit: "°C",
    leafKey: "leafTemperature",
    airKey: "airTemperature",
    leafName: "Temperatura de hoja",
    airName: "Temperatura del aire",
    leafColor: "#9E7000",
    airColor: "#D65A31",
  },
  humidity: {
    label: "Humedad",
    unit: "%",
    leafKey: "leafHumidity",
    airKey: "airHumidity",
    leafName: "Humedad de hoja",
    airName: "Humedad del aire",
    leafColor: "#2A4B3C",
    airColor: "#397BCE",
    domain: [0, 100],
  },
};

const formatTime = (value) => value
  ? new Intl.DateTimeFormat("es-ES", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "Sin datos";

const formatChartTime = (value, longRange) => new Intl.DateTimeFormat(
  "es-ES",
  longRange ? { day: "2-digit", month: "2-digit" } : { hour: "2-digit", minute: "2-digit" },
).format(new Date(value));

const connectivityLabel = (device) => {
  if (device?.lifecycle_state === "assigned") return { label: "Asignado, esperando el primer mensaje", tone: "text-moderate" };
  const state = device?.connectivity?.effective_state;
  if (state === "online") return { label: "En línea", tone: "text-stable" };
  if (state === "stale") return { label: "Sin datos recientes", tone: "text-moderate" };
  if (state === "offline") return { label: "Sin conexión", tone: "text-critical" };
  return { label: "Estado desconocido", tone: "text-ink-tertiary" };
};

function ReadingCard({ icon: Icon, label, value, unit }) {
  return (
    <Panel className="p-5">
      <div className="flex items-center gap-2 text-ink-secondary text-sm mb-2"><Icon size={16} /> {label}</div>
      <div className="font-heading font-extrabold text-3xl">{value != null ? `${value.toFixed(1)} ${unit}` : "—"}</div>
    </Panel>
  );
}

export default function SensorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [latest, setLatest] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [timeWindow, setTimeWindow] = useState("24h");
  const [chartMetric, setChartMetric] = useState("temperature");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectedWindow = TIME_WINDOWS[timeWindow];
  const selectedMetric = CHART_METRICS[chartMetric];

  const load = useCallback(async () => {
    try {
      setError(null);
      const to = new Date();
      const from = new Date(to.getTime() - selectedWindow.durationMs);
      const [deviceResult, latestResult, telemetryResult] = await Promise.all([
        getDevice(id),
        getDeviceLatest(id).catch((err) => {
          if (err instanceof TreepletApiError && err.status === 404) return null;
          throw err;
        }),
        getDeviceTelemetry(id, from.toISOString(), to.toISOString(), selectedWindow.interval),
      ]);
      setDevice(deviceResult);
      setLatest(latestResult);
      setTelemetry(telemetryResult.series || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar el dispositivo");
    } finally {
      setLoading(false);
    }
  }, [id, selectedWindow]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(load, 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const chartData = useMemo(() => telemetry.map((point) => ({
    time: formatChartTime(point.observed_at, timeWindow !== "24h"),
    leafTemperature: point.leaf_surface_temperature_c?.avg,
    airTemperature: point.air_temperature_c?.avg,
    leafHumidity: point.leaf_surface_humidity_pct?.avg,
    airHumidity: point.air_humidity_pct?.avg,
  })), [telemetry, timeWindow]);

  const hasChartData = chartData.some((point) => (
    point[selectedMetric.leafKey] != null || point[selectedMetric.airKey] != null
  ));

  if (loading) return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>;
  if (error || !device) return <div className="h-full flex flex-col items-center justify-center gap-3 text-critical"><CircleAlert size={30} /><span>{error || "Dispositivo no encontrado"}</span><button onClick={() => navigate("/sensores")} className="underline font-semibold">Volver a dispositivos</button></div>;

  const connectivity = connectivityLabel(device);
  return (
    <div className="h-full overflow-y-auto overflow-x-hidden tp-scroll animate-fade-in max-w-[1400px] mx-auto w-full pr-1 flex flex-col">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-medium text-ink-secondary hover:text-ink mb-3"><ArrowLeft size={16} /> Volver</button>
      <PageHeader
        title={device.id}
        subtitle={`${device.device_model} · ${device.field_name || "Sin campo"}`}
        testId="sensor-detail-header"
        actions={<button onClick={() => void load()} className="flex items-center gap-2 text-sm font-semibold text-brand"><RefreshCw size={15} /> Actualizar</button>}
      />

      <div className={`border border-line rounded-lg px-4 py-3 mb-4 font-semibold ${connectivity.tone}`}>
        <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-current" />{connectivity.label}</span>
      </div>

      <MildewStatus mildew={device.mildew} compact showDetailsButton className="mb-4" testId="sensor-detail-mildew" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <ReadingCard icon={Thermometer} label="Temperatura de hoja" value={latest?.measurements?.leaf_surface_temperature_c} unit="°C" />
        <ReadingCard icon={Droplets} label="Humedad de hoja" value={latest?.measurements?.leaf_surface_humidity_pct} unit="%" />
        <ReadingCard icon={Thermometer} label="Temperatura del aire" value={latest?.measurements?.air_temperature_c} unit="°C" />
        <ReadingCard icon={Droplets} label="Humedad del aire" value={latest?.measurements?.air_humidity_pct} unit="%" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 flex-1 min-h-0">
        <Panel className="p-5 xl:col-span-2 min-h-0 flex flex-col">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-5">
            <div><Overline>{selectedWindow.label}</Overline><h2 className="font-heading font-bold text-lg mt-1">Telemetría recibida</h2></div>
            <div className="flex flex-wrap gap-2">
              <select aria-label="Ventana de tiempo" value={timeWindow} onChange={(event) => setTimeWindow(event.target.value)} className="rounded-md border border-line bg-white px-2 py-1.5 text-sm font-medium text-ink">
                {Object.entries(TIME_WINDOWS).map(([value, option]) => <option key={value} value={value}>{option.label}</option>)}
              </select>
              <div className="inline-flex rounded-md border border-line p-0.5" aria-label="Métrica del gráfico">
                {Object.entries(CHART_METRICS).map(([value, option]) => <button key={value} type="button" onClick={() => setChartMetric(value)} className={`rounded px-2.5 py-1 text-sm font-semibold ${chartMetric === value ? "bg-brand text-white" : "text-ink-secondary hover:text-ink"}`}>{option.label}</button>)}
              </div>
            </div>
          </div>
          {!hasChartData ? <div className="flex-1 min-h-0 flex items-center justify-center text-ink-tertiary">Todavía no hay {selectedMetric.label.toLowerCase()} válida para este dispositivo en esta ventana.</div> :
            <div className="flex-1 min-h-0"><ResponsiveContainer width="100%" height="100%"><LineChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E5E5DE" vertical={false} /><XAxis dataKey="time" tick={{ fontSize: 11 }} /><YAxis domain={selectedMetric.domain} tick={{ fontSize: 11 }} unit={selectedMetric.unit} /><Tooltip /><Legend /><Line type="monotone" dataKey={selectedMetric.leafKey} name={selectedMetric.leafName} stroke={selectedMetric.leafColor} dot={false} strokeWidth={2} connectNulls /><Line type="monotone" dataKey={selectedMetric.airKey} name={selectedMetric.airName} stroke={selectedMetric.airColor} dot={false} strokeWidth={2} connectNulls /></LineChart></ResponsiveContainer></div>}
        </Panel>

        <Panel className="p-5 space-y-5">
          <div><Overline>Información técnica</Overline><h2 className="font-heading font-bold text-lg mt-1">Estado del dispositivo</h2></div>
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between gap-3"><span className="flex items-center gap-2 text-ink-secondary"><Clock size={15} /> Última comunicación</span><span className="font-semibold text-right">{formatTime(device.connectivity?.last_seen_at)}</span></div>
            <div className="flex items-center justify-between gap-3"><span className="flex items-center gap-2 text-ink-secondary"><Battery size={15} /> Batería</span><span className="font-semibold">{device.connectivity?.battery_voltage_v != null ? `${device.connectivity.battery_voltage_v.toFixed(2)} V` : "—"}</span></div>
            <div className="flex items-center justify-between gap-3"><span className="flex items-center gap-2 text-ink-secondary"><Signal size={15} /> Señal</span><span className="font-semibold">{device.connectivity?.signal_rssi_dbm != null ? `${device.connectivity.signal_rssi_dbm} dBm` : "—"}</span></div>
            <div className="flex items-center justify-between gap-3"><span className="text-ink-secondary">Firmware</span><span className="font-semibold">{device.connectivity?.firmware_version || device.firmware_version || "—"}</span></div>
            <div className="flex items-center justify-between gap-3"><span className="text-ink-secondary">Sensor principal</span><span className="font-semibold">{device.sensor_type}</span></div>
            <div className="flex items-start justify-between gap-3"><span className="flex items-center gap-2 text-ink-secondary"><MapPin size={15} /> Posición</span><span className="font-semibold text-right">{device.deployment?.latitude != null ? `${device.deployment.latitude.toFixed(5)}, ${device.deployment.longitude.toFixed(5)}` : "—"}</span></div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
